import { useQueries, useQuery } from "@tanstack/react-query";
import { getCurrentScope } from "../../lib/auth";
import {
  PLACE_LEVELS, fetchBreakdown, fetchPlaces, placeKey, type BreakdownLevel, type PlaceFilter, type PlaceLevel,
} from "../../lib/dashboard-data";
import { rangeKey, type Range } from "../../lib/stats-range";

/**
 * เส้นทางเจาะลงของ Dashboard (breadcrumb): รากคือขอบเขตของบัญชี แล้วต่อด้วยระดับที่เลือกใน URL
 *
 * ชื่อ/รหัสของแต่ละขั้นอ่านจาก /stats/breakdown ของขั้นแม่ (ตัวเดียวกับที่จอขั้นแม่ใช้ —
 * กดเจาะลงมาจึงได้จาก cache ทันที) ไม่เดาจาก id · ชื่อรากมาจาก /stats/places
 */

export type NodeLevel = "system" | PlaceLevel;

export interface PathNode {
  level: NodeLevel;
  /** null = ราก (ขอบเขตของบัญชีเอง) */
  id: string | null;
  name: string;
  code: string;
  /** ตัวกรองที่ทำให้จอแสดงขั้นนี้ */
  filter: PlaceFilter;
  /** พื้นที่ระดับเดียวกันภายใต้ขั้นแม่ (สลับแขวง/เมือง/ตาแสงได้โดยไม่ต้องย้อนขึ้นไป) — รากไม่มี */
  siblings: { id: string; name: string }[];
  /** ตัวกรองของขั้นแม่ — สลับไปพื้นที่อื่น = ตัวกรองนี้ + id ใหม่ */
  parentFilter: PlaceFilter;
}

const LEVEL_WORD: Record<NodeLevel, string> = {
  system: "ທົ່ວປະເທດ",
  province: "ແຂວງ",
  district: "ເມືອງ",
  tasaeng: "ຕາແສງ",
  village: "ບ້ານ",
};

/** คำเรียกพื้นที่ลูกตามระดับที่ server ตอบ */
export const CHILD_WORD: Record<BreakdownLevel, string> = {
  province: "ແຂວງ",
  district: "ເມືອງ",
  tasaeng: "ຕາແສງ",
  village: "ບ້ານ",
  none: "",
};

/** ชื่อเต็มสำหรับหัวจอ: ເມືອງ + ชื่อ (ชื่อในทะเบียนบางแถวมีคำนำหน้าอยู่แล้ว) */
export function placeTitle(level: NodeLevel, name: string): string {
  if (level === "system" || level === "province") return name;
  const word = LEVEL_WORD[level];
  return name.startsWith(word) ? name : `${word}${name}`;
}

/** ระดับของรากตาม scope ใน token — ใช้แสดงผลเท่านั้น server กรองเองอีกชั้น */
function rootLevel(): NodeLevel {
  const s = getCurrentScope();
  switch (s?.level) {
    case "province":
      return "province";
    case "district":
      return "district";
    case "tasaeng":
      return "tasaeng";
    case "village":
      return "village";
    default:
      return "system";
  }
}

export function useScopePath(range: Range, place: PlaceFilter) {
  const root = rootLevel();
  const places = useQuery({ queryKey: ["stats", "places"], queryFn: fetchPlaces, staleTime: 5 * 60_000 });
  const firstDistrict = places.data?.districts[0];

  // ระดับที่ผู้ใช้เจาะลงมา (เรียงจากบนลงล่าง) — ต้องลึกกว่าราก
  const rootIdx = root === "system" ? -1 : PLACE_LEVELS.indexOf(root);
  const chosen = PLACE_LEVELS.filter((l, i) => i > rootIdx && place[l]);
  const filters: PlaceFilter[] = chosen.map((_, i) => {
    const f: PlaceFilter = {};
    for (const l of chosen.slice(0, i + 1)) f[l] = place[l];
    return f;
  });
  const parentFilters: PlaceFilter[] = filters.map((_, i) => (i === 0 ? {} : (filters[i - 1] ?? {})));

  const parents = useQueries({
    queries: parentFilters.map((pf) => ({
      queryKey: ["stats", "breakdown", `${rangeKey(range)}|${placeKey(pf)}`],
      queryFn: () => fetchBreakdown(range, pf),
    })),
  });

  const rootNode: PathNode = {
    level: root,
    id: null,
    filter: {},
    siblings: [],
    parentFilter: {},
    code: root === "district" ? (firstDistrict?.code ?? "") : "",
    name:
      root === "system"
        ? "ທົ່ວປະເທດ"
        : root === "province"
          ? (firstDistrict?.parent_name_lo ?? "ແຂວງຂອງທ່ານ")
          : root === "district"
            ? placeTitle("district", firstDistrict?.name_lo ?? "")
            : "ເຂດຂອງທ່ານ",
  };

  const nodes: PathNode[] = [rootNode];
  chosen.forEach((level, i) => {
    const id = place[level] ?? null;
    const items = parents[i]?.data?.items ?? [];
    const row = items.find((r) => r.place_id === id);
    nodes.push({
      level,
      id,
      code: row?.code ?? "",
      name: row ? placeTitle(level, row.name_lo) : "…",
      filter: filters[i] ?? {},
      parentFilter: parentFilters[i] ?? {},
      siblings: items
        .filter((r): r is typeof r & { place_id: string } => r.place_id !== null)
        .map((r) => ({ id: r.place_id, name: placeTitle(level, r.name_lo) }))
        .sort((a, b) => a.name.localeCompare(b.name, "lo")),
    });
  });

  return { nodes, here: nodes[nodes.length - 1] ?? rootNode, places: places.data };
}
